import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Tooltip, useTooltip } from '@kizen/kds/Tooltip';
import { useSet } from 'hooks/useSet';
import { BorderRadii, borderRadii, layers } from '__app/spacing';
import {
  FontSize,
  TextTransform,
  KizenTypography,
  KizenTypographyProps,
  fontSizes,
  fontWeights,
} from '__app/typography';
import Icon from '__components/Kizen/Icon';

type Option<T = string> = {
  label: string;
  value: T;
  iconName?: string;
  disabled?: boolean;
};

type ButtonRadioProps<T = string> = {
  options: Option<T>[];
  value: T;
  onChange(value: T): void;
  height?: string;
  label?: string;
  gap?: number;
  flex?: number;
  color?: string;
  fontColor?: string;
  selectedColor?: string;
  backgroundColor?: string;
  hoverColor?: string | false;
  variant?: 'outline' | 'underline';
  optionHorizontalPadding?: number;
  optionWidth?: string;
  width?: string;
  bold?: boolean;
  textTransform?: TextTransform;
  fontSize?: FontSize;
  borderRadius?: BorderRadii;
  /**
   * If true, the background color will be the same as the font color when selected.
   * The border color will not change when selected. Setting this to true also sets
   * the unselected background color to that of the `selectedColor` prop.
   */
  highlightBackground?: boolean;
  /** Disables every option, on top of any per-option `disabled`. */
  disabled?: boolean;
  truncate?: boolean;
  centered?: boolean;
};

type ButtonMultiSelectProps<T = string> = Omit<
  ButtonRadioProps,
  'options' | 'value' | 'onChange' | 'highlightBackground'
> & {
  options: Option<T>[];
  value: T[];
  onChange(value: T[]): void;
};

type RadioOptionProps<T = string> = Pick<
  ButtonRadioProps,
  | 'color'
  | 'fontColor'
  | 'selectedColor'
  | 'hoverColor'
  | 'truncate'
  | 'height'
  | 'flex'
  | 'highlightBackground'
  | 'optionWidth'
  | 'variant'
> & {
  option: Option<T>;
  selected: boolean;
  disabled?: boolean;
  onClick(ev: MouseEvent<HTMLDivElement>): void;
  onKeyDown?(ev: KeyboardEvent<HTMLDivElement>): void;
  tabIndex?: number;
  optionRef?: (node: HTMLDivElement | null) => void;
  hasGap?: boolean;
  hideBorder?: boolean;
  horizontalPadding?: number;
  borderRadius?: string;
  fontSize?: string;
  fontWeight?: number;
  textTransform?: string;
};

type StyledRadioOptionProps = Omit<RadioOptionProps, 'option' | 'variant'>;

const colorTransition = css`
  transition:
    color 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
`;

const radioOptionStyles = ({
  color,
  disabled = false,
  flex = 1,
  fontSize,
  fontWeight,
  fontColor,
  hideBorder = false,
  highlightBackground = false,
  selected,
  selectedColor = 'var(--color-button-secondary-default)',
  hoverColor = 'var(--color-button-secondary-hover)',
  textTransform,
  optionWidth,
  truncate = false,
}: StyledRadioOptionProps) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${fontColor};
  border: 1px solid ${color};
  border-right: none;
  ${truncate ? 'flex: 0 1 auto; min-width: 0;' : `flex: ${flex};`}
  ${colorTransition}
  ${fontSize && `font-size: ${fontSize};`}
  ${fontWeight && `font-weight: ${fontWeight};`}
  ${textTransform && `text-transform: ${textTransform};`}
  ${highlightBackground && `background-color: ${selectedColor};`}
  ${selected &&
  !disabled &&
  css`
    && {
      color: ${selectedColor};
      ${highlightBackground
        ? `background-color: ${color};`
        : `border-color: ${selectedColor};`}
    }
  `}
  ${hideBorder &&
  css`
    border: none;
    align-self: flex-end;
  `}
  ${hoverColor !== false &&
  !disabled &&
  css`
    &:hover {
      color: ${selectedColor};
      border-color: ${hoverColor || selectedColor};
      ${highlightBackground && `background-color: ${hoverColor || fontColor};`}
    }
  `}
  ${Boolean(optionWidth) &&
  css`
    width: ${optionWidth};
    max-width: ${optionWidth};
  `}
  ${disabled &&
  css`
    && {
      cursor: not-allowed;
      color: var(--color-font-placeholder);
      border-color: var(--color-border-medium);
      background-color: var(--color-background-white);
    }
  `}

  i,
  i:hover {
    font-size: 16px;
    color: inherit;
  }

  i svg {
    font-size: unset;
  }
`;

const Container = styled.div<Pick<ButtonRadioProps, 'flex'>>`
  flex: ${({ flex = 1 }) => flex};
  display: flex;
  flex-direction: column;
`;

const Label = styled(KizenTypography)<KizenTypographyProps>`
  flex: 0.3;
  color: var(--color-font-placeholder);
  margin-top: 4px;
`;

const RadioContainer = styled('div', {
  shouldForwardProp: (prop) =>
    !['gap', 'variant', 'width', 'truncate', 'centered'].includes(prop),
})<
  Pick<ButtonRadioProps, 'gap' | 'variant' | 'width' | 'truncate' | 'centered'>
>`
  flex: 0.7;
  display: flex;
  z-index: ${layers.content(0, 0)};

  ${({ truncate }) =>
    truncate &&
    css`
      flex: 0 1 auto;
      max-width: 100%;
    `}

  ${({ centered }) =>
    centered &&
    css`
      align-self: center;
    `}

  ${({ gap }) =>
    Boolean(gap) &&
    css`
      gap: ${gap}px;
    `}

  ${({ variant }) =>
    variant === 'outline' &&
    css`
      align-items: flex-end;
    `}

  ${({ width }) =>
    Boolean(width) &&
    css`
      width: ${width};
    `}
`;

const TruncatedLabel = styled.span`
  font: inherit;
  display: inline-block;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const UnderlineRadioOption = styled.div<StyledRadioOptionProps>`
  ${radioOptionStyles}
  border-left: none;
  border-top: none;
`;

const OutlineRadioOption = styled.div<StyledRadioOptionProps>`
  ${radioOptionStyles}
  ${({ height = '24px' }) => height && `height: ${height};`}
  border: 1px solid ${({ color = 'var(--color-border-medium)' }) => color};
  ${({ horizontalPadding = 0 }) =>
    horizontalPadding > 0 &&
    css`
      padding-left: ${horizontalPadding}px;
      padding-right: ${horizontalPadding}px;
    `}

  ${({ selected, hoverColor, disabled }) => css`
    ${selected &&
    css`
      z-index: ${layers.content(0, 1)};
    `}
    ${hoverColor !== false &&
    !disabled &&
    css`
      &:hover {
        z-index: ${layers.content(0, 2)};
      }
    `}
  `}


    
  &:last-child {
    ${({ borderRadius = 'small' }) => css`
      border-top-right-radius: ${borderRadius};
      border-bottom-right-radius: ${borderRadius};
    `}
  }
  &:first-child {
    ${({ borderRadius = 'small' }) => css`
      border-top-left-radius: ${borderRadius};
      border-bottom-left-radius: ${borderRadius};
    `}
  }
  &:not(:last-child) {
    margin-right: -1px;
  }

  ${({ hasGap, borderRadius = 'small' }) =>
    hasGap &&
    css`
      border-radius: ${borderRadius};
    `}
`;

const RadioOption = <T,>(props: RadioOptionProps<T>) => {
  const {
    variant,
    option,
    selected,
    disabled,
    flex,
    hasGap,
    hideBorder,
    onClick,
    onKeyDown,
    tabIndex,
    optionRef,
    horizontalPadding,
    height,
    borderRadius,
    color,
    selectedColor,
    hoverColor,
    textTransform,
    fontSize,
    fontWeight,
    fontColor,
    highlightBackground,
    optionWidth,
    truncate,
  } = props;

  // Icons have no visible label at all, so their tooltip always shows; a text label's
  // tooltip only shows when the truncated text is actually overflowing.
  const { showTooltip, triggerProps, targetProps, tooltipProps } =
    useTooltip(true);

  const [labelOverflowing, setLabelOverflowing] = useState(false);

  const handleMouseOver = useCallback(() => {
    const el = targetProps.ref.current;

    setLabelOverflowing(el ? el.scrollWidth - el.clientWidth > 1 : false);
    triggerProps.onMouseOver?.();
  }, [targetProps, triggerProps]);

  const showLabelTooltip = showTooltip && labelOverflowing;

  const Option =
    variant === 'outline' ? OutlineRadioOption : UnderlineRadioOption;

  return (
    <Option
      {...triggerProps}
      onMouseOver={handleMouseOver}
      ref={optionRef}
      key={String(option.value)}
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      selected={selected}
      disabled={disabled}
      tabIndex={disabled ? undefined : tabIndex}
      data-qa-id={`${option.label}-${selected ? 'selected' : 'unselected'}`}
      onClick={disabled ? () => undefined : onClick}
      onKeyDown={disabled ? undefined : onKeyDown}
      flex={flex}
      hasGap={hasGap}
      hideBorder={hideBorder}
      horizontalPadding={horizontalPadding}
      height={height}
      borderRadius={borderRadius}
      color={color}
      selectedColor={selectedColor}
      hoverColor={hoverColor}
      textTransform={textTransform}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontColor={fontColor}
      highlightBackground={highlightBackground}
      optionWidth={optionWidth}
      truncate={truncate}
      data-qa="underline-radio-option"
    >
      {option.iconName ? (
        <>
          <Icon icon={option.iconName} {...targetProps} />
          {showTooltip && (
            <Tooltip
              position="top"
              text={option.label}
              modalCompatibility
              {...tooltipProps}
            />
          )}
        </>
      ) : truncate ? (
        <>
          <TruncatedLabel {...targetProps}>{option.label}</TruncatedLabel>
          {showLabelTooltip && (
            <Tooltip
              position="top"
              text={option.label}
              modalCompatibility
              {...tooltipProps}
            />
          )}
        </>
      ) : (
        option.label
      )}
    </Option>
  );
};

export const ButtonRadio = <T,>({
  options,
  value,
  onChange,
  height,
  variant = 'underline',
  label = '',
  gap = 0,
  flex = 1,
  optionHorizontalPadding = 0,
  bold = false,
  textTransform = 'none',
  fontSize = 'text',
  borderRadius = 'small',
  color = 'var(--color-border-medium)',
  fontColor = 'var(--color-font-primary)',
  selectedColor = 'var(--color-button-secondary-default)',
  hoverColor = 'var(--color-button-secondary-hover)',
  highlightBackground = false,
  backgroundColor,
  optionWidth,
  width,
  disabled = false,
  truncate = false,
  centered = false,
  ...rest
}: ButtonRadioProps<T>) => {
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hasValue = options.some((opt) => opt.value === value);

  const handleOptionKeyDown = useCallback(
    (currentValue: T, ev: KeyboardEvent<HTMLDivElement>) => {
      if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft') {
        return;
      }

      ev.preventDefault();

      const enabled = options.filter((o) => !(disabled || o.disabled));

      if (enabled.length === 0) {
        return;
      }

      const currentIndex = enabled.findIndex((o) => o.value === currentValue);
      const delta = ev.key === 'ArrowRight' ? 1 : -1;
      const nextIndex =
        (currentIndex + delta + enabled.length) % enabled.length;
      const next = enabled[nextIndex];

      if (!next) {
        return;
      }

      onChange(next.value);
      optionRefs.current[String(next.value)]?.focus();
    },
    [options, disabled, onChange]
  );

  return (
    <Container flex={flex} data-qa-button-toggle={label} {...rest}>
      {label && <Label type="micro">{label}</Label>}
      <RadioContainer
        variant={variant}
        gap={gap}
        width={width}
        truncate={truncate}
        centered={centered}
        role="radiogroup"
        aria-label={label || undefined}
      >
        {options.map((opt, _, arr) => {
          const selected = opt.value === value;
          const optionDisabled = disabled || Boolean(opt.disabled);
          const isTabbable = hasValue
            ? selected
            : opt.value === arr.find((o) => !(disabled || o.disabled))?.value;

          return (
            <RadioOption
              key={String(opt.value)}
              variant={variant}
              option={opt}
              selected={selected}
              disabled={optionDisabled}
              tabIndex={isTabbable ? 0 : -1}
              optionRef={(node) => {
                optionRefs.current[String(opt.value)] = node;
              }}
              data-qa-id={`${opt.label}-${selected ? 'selected' : 'unselected'}`}
              flex={1 / arr.length}
              truncate={truncate}
              hasGap={Boolean(gap)}
              hideBorder={!!opt.iconName}
              onClick={() => onChange(opt.value)}
              onKeyDown={(ev) => handleOptionKeyDown(opt.value, ev)}
              horizontalPadding={optionHorizontalPadding}
              height={height}
              borderRadius={borderRadii[borderRadius]}
              color={color}
              selectedColor={selectedColor}
              hoverColor={hoverColor}
              textTransform={textTransform}
              fontSize={fontSizes[fontSize]}
              fontWeight={fontWeights[bold ? 'bold' : 'regular']}
              fontColor={fontColor}
              highlightBackground={highlightBackground}
              optionWidth={optionWidth}
              data-qa="underline-radio-option"
            />
          );
        })}
      </RadioContainer>
    </Container>
  );
};

export const ButtonMultiSelect = ({
  options,
  value,
  onChange,
  height,
  variant = 'underline',
  label = '',
  gap = 0,
  flex = 1,
  optionHorizontalPadding = 0,
  bold = false,
  textTransform = 'none',
  fontSize = 'text',
  borderRadius = 'small',
  color = 'var(--color-border-medium)',
  fontColor = 'var(--color-background-darker)',
  selectedColor = 'var(--color-button-secondary-default)',
  hoverColor = 'var(--color-button-secondary-hover)',
  width,
  optionWidth,
  ...rest
}: ButtonMultiSelectProps) => {
  const [, { has, toggle }] = useSet(value);

  const Option =
    variant === 'outline' ? OutlineRadioOption : UnderlineRadioOption;

  return (
    <Container {...rest}>
      {label ? <Label type="micro">{label}</Label> : null}
      <RadioContainer variant={variant} width={width}>
        {options.map((opt, _, arr) => (
          <Option
            key={String(opt.value)}
            selected={has(opt.value)}
            data-qa-id={`${opt.label ?? opt.iconName}-${has(opt.value) ? 'selected' : 'unselected'}`}
            flex={1 / arr.length}
            hasGap={Boolean(gap)}
            hideBorder={!!opt.iconName}
            onClick={() => onChange(toggle(opt.value))}
            horizontalPadding={optionHorizontalPadding}
            height={height}
            borderRadius={borderRadii[borderRadius]}
            color={color}
            selectedColor={selectedColor}
            hoverColor={hoverColor}
            textTransform={textTransform}
            fontSize={fontSizes[fontSize]}
            fontWeight={fontWeights[bold ? 'bold' : 'regular']}
            fontColor={fontColor}
            optionWidth={optionWidth}
          >
            {opt.label}
          </Option>
        ))}
      </RadioContainer>
    </Container>
  );
};
